/**
 * #13 歷史送禮清單 — LIFF /gift/history
 *
 * 顯示 sender 送出的所有禮物(GET /api/gifts/sent)。
 * 簡單列表:狀態 + 商品摘要 + 收禮人(備註或實名)+ 時間,點任一筆跳 /gift/sent/:code 詳情。
 *
 * 空狀態:引導去 /gift/new 開新禮物。
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Gift as GiftIcon } from "lucide-react";
import StatusMark from "../components/StatusMark";
import { listMyGiftsSent, type Gift, GIFT_STATUS_LABELS, GIFT_TEMPLATES } from "../api/gifts";
import { useUIStore } from "../store/uiStore";


// status → 顏色 mapping(讓 user 一眼能掃)
const STATUS_COLOR: Record<string, string> = {
  pending_payment_mock: "text-coral",
  paid: "text-primary",
  delivered: "text-primary",
  received: "text-sage",
  cancelled: "text-muted",
};


export default function GiftHistory() {
  const navigate = useNavigate();
  const showToast = useUIStore((s) => s.showToast);
  const [gifts, setGifts] = useState<Gift[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const list = await listMyGiftsSent();
        setGifts(list);
      } catch {
        showToast("載入歷史失敗", "error");
        setGifts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [showToast]);

  if (loading) {
    return <div className="min-h-screen bg-cream flex items-center justify-center text-muted text-sm">載入中…</div>;
  }

  return (
    <div className="min-h-screen bg-cream pb-12">
      <header className="bg-primary text-white px-5 py-6 flex items-center gap-3">
        <button onClick={() => navigate("/home")} className="text-white text-xl" aria-label="返回">‹</button>
        <div className="flex-1">
          <h1 className="text-lg font-bold">歷史送禮</h1>
          <div className="text-xs opacity-80 mt-0.5">您送出的私享禮遇</div>
        </div>
      </header>

      <div className="px-5 mt-5 space-y-3">
        {(!gifts || gifts.length === 0) ? (
          <EmptyState onStart={() => navigate("/gift/new")} />
        ) : (
          gifts.map((g) => (
            <GiftRow
              key={g.gift_id}
              gift={g}
              onClick={() => navigate(`/gift/sent/${g.short_code || g.gift_id}`)}
            />
          ))
        )}
      </div>

      {gifts && gifts.length > 0 && (
        <div className="px-5 mt-5">
          <button onClick={() => navigate("/gift/new")} className="btn-secondary w-full">
            送出新禮物
          </button>
        </div>
      )}
    </div>
  );
}


function GiftRow({ gift, onClick }: { gift: Gift; onClick: () => void }) {
  const itemsSummary = formatItemsSummary(gift.items);
  const recipientLabel = gift.recipient_name || "貴賓";
  const dateStr = formatDate(gift.created_at);
  const tpl = GIFT_TEMPLATES.find((t) => t.key === gift.blessing_card?.template);
  const statusColor = STATUS_COLOR[gift.status] || "text-ink";
  const statusLabel = GIFT_STATUS_LABELS[gift.status] || gift.status;

  return (
    <button
      onClick={onClick}
      className="card w-full text-left p-4 active:bg-white/80 transition"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-bold ${statusColor}`}>● {statusLabel}</span>
            {tpl && <span className="text-xxs text-muted">· {tpl.label}祝福卡</span>}
          </div>
          <div className="text-sm font-bold text-ink truncate">{itemsSummary}</div>
          <div className="text-xs text-muted mt-1">
            給 {recipientLabel}
            <span className="ml-2">· {gift.total_bottles} 瓶</span>
          </div>
        </div>
        <div className="text-xs text-muted text-right shrink-0 mt-1">
          {dateStr}
        </div>
      </div>
      {gift.short_code && (
        <div className="text-xxs text-muted mt-2 font-mono opacity-70">
          #{gift.short_code}
        </div>
      )}
    </button>
  );
}


function EmptyState({ onStart }: { onStart: () => void }) {
  return (
    <div className="card text-center py-10 space-y-4">
      <StatusMark icon={GiftIcon} />
      <div className="text-base text-ink">尚未送出任何禮物</div>
      <div className="text-xs text-muted">以霽雲之名,獻上一份恆久的滋養。</div>
      <button onClick={onStart} className="btn-primary mt-2">
        送出第一份禮物
      </button>
    </div>
  );
}


// ── helpers ──

function formatItemsSummary(items: Gift["items"]): string {
  if (!items || items.length === 0) return "—";
  const first = items[0];
  const firstStr = `${first.name} × ${first.qty}`;
  if (items.length === 1) return firstStr;
  return `${firstStr} 等 ${items.length} 項`;
}

function formatDate(raw?: string): string {
  if (!raw) return "";
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return "";
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) {
      return d.toLocaleTimeString("zh-TW", { hour: "2-digit", minute: "2-digit" });
    }
    return d.toLocaleDateString("zh-TW", { month: "numeric", day: "numeric" });
  } catch {
    return "";
  }
}
